import React, { useEffect, useState } from 'react'
import { Channel as StreamChannel } from 'stream-chat'
import type { ChannelMemberResponse } from 'stream-chat'
import { t } from 'ttag'
import YumDialog from '../YumDialog/YumDialog'
import YumDialogTitle from '../YumDialogTitle/YumDialogTitle'
import YumSearchField from '../YumSearchField/YumSearchField'
import Loader from '../Loader/Loader'

// Stream allows querying at most 100 members per request; the search field uses server-side
// autocomplete, so even very large channels stay searchable beyond the first page.
const MEMBERS_PAGE_SIZE = 100

export interface IYumChatMembersModalProps {
  open: boolean
  onClose(): void
  channel?: StreamChannel
}

export default function YumChatMembersModal(props: IYumChatMembersModalProps) {
  const { open, onClose, channel } = props

  const [search, setSearch] = useState('')
  const [members, setMembers] = useState<ChannelMemberResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  useEffect(() => {
    if (!open || !channel) return
    let cancelled = false
    const query = search.trim()
    setLoading(true)
    channel
      // @ts-ignore // name $autocomplete is a valid member filter
      .queryMembers(query ? { name: { $autocomplete: query } } : {}, {}, { limit: MEMBERS_PAGE_SIZE })
      .then((response) => {
        if (!cancelled) setMembers(response.members)
      })
      .catch((e: any) => console.error('queryMembers error: ', e))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, channel, search])

  // @ts-ignore // member_count exists on channel data
  const memberCount: number = channel?.data?.member_count || 0

  return (
    <YumDialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <YumDialogTitle handleClose={onClose}>
        {t`Members`}
        {/* Solid accessible gray instead of opacity — half-transparent title ink fell under 4.5:1. */}
        {memberCount > 0 && <span style={{ color: '#6b6b76', whiteSpace: 'nowrap' }}> ({memberCount})</span>}
      </YumDialogTitle>
      <div className="chat-members-modal">
        <YumSearchField
          value={search}
          onChange={setSearch}
          placeholder={t`Search members...`}
          autoFocus={false}
          debounceTime={200}
          onClear={() => setSearch('')}
          style={{ marginBottom: '12px' }}
        />
        {loading ? (
          <Loader top={true} />
        ) : !members.length ? (
          <div className="chat-members-modal__empty">
            {search.trim() ? t`No members match your search.` : t`No members found.`}
          </div>
        ) : (
          <div className="chat-members-modal__list">
            {members.map((member) => {
              const name = member.user?.name || member.user?.id || ''
              return (
                <div key={member.user?.id || name} className="chat-members-modal__member">
                  {member.user?.image ? (
                    <img className="chat-members-modal__avatar" src={member.user.image} alt="" />
                  ) : (
                    <span
                      className="chat-members-modal__avatar chat-members-modal__avatar--initials"
                      aria-hidden="true"
                    >
                      {(name[0] || '?').toUpperCase()}
                    </span>
                  )}
                  <span className="chat-members-modal__name">{name}</span>
                  {member.user?.online && <span className="chat-members-modal__online" aria-label={t`Online`} />}
                </div>
              )
            })}
            {!search.trim() && memberCount > members.length && (
              <div className="chat-members-modal__more">{t`Search to find more members...`}</div>
            )}
          </div>
        )}
      </div>
    </YumDialog>
  )
}
